/**
 * Display Text Utilities
 * Converts wedding terminology to event terminology for frontend display
 * Backend systems remain unchanged - this only affects what users see
 */

export const displayTextMappings = {
  // Exact word replacements
  'Wedding': 'Event',
  'wedding': 'event',
  'Weddings': 'Events', 
  'weddings': 'events',
  
  // Compound phrases
  'Wedding Planning': 'Event Planning',
  'wedding planning': 'event planning',
  'Wedding Package': 'Event Package',
  'wedding package': 'event package',
  'Wedding Packages': 'Event Packages',
  'wedding packages': 'event packages',
  'Wedding Timeline': 'Event Timeline',
  'wedding timeline': 'event timeline',
  'Wedding Vendor': 'Event Vendor',
  'wedding vendor': 'event vendor',
  'Wedding Vendors': 'Event Vendors',
  'wedding vendors': 'event vendors',
  'Wedding Date': 'Event Date',
  'wedding date': 'event date',
  'Wedding Time': 'Event Time',
  'wedding time': 'event time',
  'Wedding Venue': 'Event Venue',
  'wedding venue': 'event venue',
  'Wedding Guest': 'Event Guest',
  'wedding guest': 'event guest',
  'Wedding Guests': 'Event Guests',
  'wedding guests': 'event guests',
  'Wedding Budget': 'Event Budget',
  'wedding budget': 'event budget',
  'Wedding Details': 'Event Details',
  'wedding details': 'event details',
  'Wedding Information': 'Event Information',
  'wedding information': 'event information',
  'Wedding Coordination': 'Event Coordination',
  'wedding coordination': 'event coordination',
  'Wedding Management': 'Event Management',
  'wedding management': 'event management',
  'Wedding Services': 'Event Services',
  'wedding services': 'event services',
  'Wedding Ceremony': 'Event Ceremony',
  'wedding ceremony': 'event ceremony',
  'Wedding Reception': 'Event Reception',
  'wedding reception': 'event reception',
  'Wedding Day': 'Event Day',
  'wedding day': 'event day',
  'Wedding Booking': 'Event Booking',
  'wedding booking': 'event booking',
  'Wedding Bookings': 'Event Bookings',
  'wedding bookings': 'event bookings',
  
  // Specific venue references
  'Premium Wedding Venue': 'Premium Event Venue',
  'premium wedding venue': 'premium event venue',
  'Wedding Venue Management': 'Event Venue Management',
  'wedding venue management': 'event venue management',
  
  // Actions and processes
  'Create Wedding': 'Create Event',
  'create wedding': 'create event',
  'Edit Wedding': 'Edit Event',
  'edit wedding': 'edit event',
  'Update Wedding': 'Update Event',
  'update wedding': 'update event',
  'Delete Wedding': 'Delete Event',
  'delete wedding': 'delete event',
  'Plan Wedding': 'Plan Event',
  'plan wedding': 'plan event',
  'Manage Wedding': 'Manage Event',
  'manage wedding': 'manage event',
  
  // Status and notifications
  'Wedding created': 'Event created',
  'wedding created': 'event created',
  'Wedding updated': 'Event updated',
  'wedding updated': 'event updated',
  'Wedding deleted': 'Event deleted',
  'wedding deleted': 'event deleted',
  'Wedding booked': 'Event booked',
  'wedding booked': 'event booked',
  'Wedding confirmed': 'Event confirmed',
  'wedding confirmed': 'event confirmed',
  'Wedding cancelled': 'Event cancelled',
  'wedding cancelled': 'event cancelled',
};

/**
 * Converts wedding terminology to event terminology in display text
 */
export const formatDisplayText = (text: string): string => {
  if (!text) return text;
  
  let result = text;
  
  // Apply all mappings with enhanced regex patterns
  Object.entries(displayTextMappings).forEach(([weddingTerm, eventTerm]) => {
    // Enhanced regex to handle possessive forms and word boundaries
    const escapedTerm = weddingTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    
    // Handle possessive forms (e.g., "John's Wedding" -> "John's Event")
    const possessiveRegex = new RegExp(`(\\w+['']s\\s+)${escapedTerm}\\b`, 'gi');
    result = result.replace(possessiveRegex, `$1${eventTerm}`);
    
    // Handle regular word boundaries
    const regularRegex = new RegExp(`\\b${escapedTerm}\\b`, 'g');
    result = result.replace(regularRegex, eventTerm);
  });
  
  return result;
};

/**
 * Clean up and format wedding/event titles
 * Removes duplicates and applies event terminology conversion
 */
export const formatEventTitle = (title: string): string => {
  if (!title) return '';
  
  // Remove duplicate words (like "Wedding Wedding")
  const words = title.split(' ');
  const cleanWords = words.filter((word, index) => {
    // Keep the word if it's the first occurrence or different from the previous word
    return index === 0 || word.toLowerCase() !== words[index - 1].toLowerCase();
  });
  
  const cleanTitle = cleanWords.join(' ');
  
  // Apply event terminology conversion
  return formatDisplayText(cleanTitle);
};

/**
 * Extract clean time slot information from wedding/event data
 * Handles various data sources and removes contract IDs systematically
 */
export const getTimeSlotDisplay = (eventData: any): string => {
  if (!eventData) return "Time not set";
  
  let timeSlot = '';
  
  // Check timeSlot field first
  if (eventData.timeSlot && !eventData.timeSlot.includes('CONTRACT_ID')) {
    timeSlot = eventData.timeSlot;
  }
  // Check weddingTime field
  else if (eventData.weddingTime && !eventData.weddingTime.includes('CONTRACT_ID')) {
    timeSlot = eventData.weddingTime;
  }
  // Extract from notes field
  else if (eventData.notes) {
    const timeSlotMatch = eventData.notes.match(/Time Slot: ([^,\[\n]+)/);
    if (timeSlotMatch) {
      timeSlot = timeSlotMatch[1].trim();
    }
  }
  
  // Clean up any contract IDs that might be present
  timeSlot = timeSlot.replace(/\s*\[CONTRACT_ID:[^\]]+\]/g, '').trim();
  
  // If still empty or contains contract references, return default business hours
  if (!timeSlot || timeSlot.includes('CONTRACT_ID') || timeSlot.length < 3) {
    return "6:00 PM to 10:00 PM";
  }
  
  return timeSlot;
};

/**
 * Get event type display name based on backend data
 */
export const getEventTypeDisplay = (eventData: any): string => {
  if (!eventData) return 'Event';
  
  const notes = eventData.notes?.toLowerCase() || '';
  const title = eventData.title?.toLowerCase() || '';
  
  if (notes.includes('corporate') || title.includes('corporate') || 
      notes.includes('conference') || title.includes('conference')) {
    return 'Corporate Event';
  }
  
  if (notes.includes('birthday') || title.includes('birthday')) {
    return 'Birthday Celebration';
  }
  
  if (notes.includes('anniversary') || title.includes('anniversary')) {
    return 'Anniversary Celebration';
  }
  
  if (notes.includes('engagement') || title.includes('engagement')) {
    return 'Engagement Party';
  }
  
  if (notes.includes('reception') || title.includes('reception')) {
    return 'Reception Event';
  }
  
  return 'Wedding Celebration';
};

export default {
  format: formatDisplayText,
  formatTitle: formatEventTitle,
  getEventType: getEventTypeDisplay,
  getTimeSlot: getTimeSlotDisplay,
  mappings: displayTextMappings
};